import { type GalleryFileLike, galleryFileBackendId } from '../utils/galleryFileBackendId';
import type { PickerFile } from './filePicker.context';

export type { GalleryFileLike };

/** Bytes. GraphQL sends the count as a string; a picked file already has a number. */
const bytes = (size: GalleryFileLike['size']): number => {
  const parsed = Number(size);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

/**
 * One gallery file as the picker's seed, or undefined when it has no backend id to match on.
 *
 * A file with no backend id is dropped rather than seeded: there is nothing for the picker to match
 * it to, so it would render unticked, clicking it would append a second copy of itself, and its
 * chip's remove button would drop every other seeded file with it.
 *
 * What comes out is named by the numeric id, because that is all a GraphQL `File` carries. The
 * picker keys its selection by uuid, so the app resolves these before opening — see the dashboard's
 * `useResolvePickerSeed`, which is where the local `files` row can actually be read.
 */
export const pickerFileFromGalleryFile = (file?: GalleryFileLike | null): PickerFile | undefined => {
  if (!file) {
    return undefined;
  }

  const laravelId = galleryFileBackendId(file);

  if (laravelId === undefined) {
    return undefined;
  }

  return {
    // Restated from the id we matched on rather than copied, so the two can never disagree — this
    // is the value the gallery renders images by and `getFilesIds` reads back out on save.
    id: String(laravelId),
    laravel_id: laravelId,
    name: file.name ?? '',
    size: bytes(file.size),
    type: file.type ?? '',
    extension: file.extension ?? '',
  };
};

/** Every gallery file that can be matched, in the order the field already had them. */
export const pickerFilesFromGalleryFiles = (
  files?: readonly (GalleryFileLike | null | undefined)[] | null,
): PickerFile[] =>
  (files ?? []).reduce<PickerFile[]>((picked, file) => {
    const pickerFile = pickerFileFromGalleryFile(file);

    if (pickerFile) {
      picked.push(pickerFile);
    }

    return picked;
  }, []);
